import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import Transport from './Transport';
import { staticsUrl, baseUrl } from '../../../config';
import historyCache from '../../../config/historyCache.json';
import { getLocalSettings } from '../../../redux/actions';
import PlayIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';

const useStyles = makeStyles(theme => ({
  frame: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'stretch',
    background: '#eee',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  startStop: {
    position: 'absolute',
    top: '15%',
    bottom: '30%',
    left: '20%',
    right: '20%',
    '& svg': {
      color: '#fff',
      fontSize: 150,
    },
  },
  playBtn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    inset: 0,
    opacity: 0,
  },
  imgWrap: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  imgBack: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    heigt: '100%',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    overflow: 'hidden',
    opacity: 0.4,
    filter: 'blur(5px)',
  },
  imgMain: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    heigt: '100%',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
  },
  imgContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    '& > div': {
      position: 'absolute',
      inset: 0,
      width: '100%',
      heigt: '100%',
    },
  },
  preloads: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0.01,
    width: 1,
    height: 1,
  },
  en: {
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '1.35em',
    padding: '0.5em 0.5em 0 0.5em',
  },
  pl: {
    textAlign: 'center',
    fontSize: '1.35em',
    padding: '0 0.5em 0.5em 0.5em',
  },
  '@media(max-width: 850px)': {
    playBtn: {
      '& svg': {
        fontSize: 90,
      },
    },
  },
  '@media(max-width: 500px)': {
    playBtn: {
      '& svg': {
        fontSize: 60,
      },
    },
  },
}));

function Image(props) {
  const classes = useStyles();
  const { no, exercise, imgRef, loadedImages, loading, throttle, saveBandwidth } = props;
  const { text, translation, imageUrl } = exercise[no];
  const mainImgUrl = staticsUrl + imageUrl;
  const fallbackImg = historyCache[imageUrl];
  const fallBackUrl = fallbackImg ? `${baseUrl}/resources/History/${historyCache[imageUrl]}` : mainImgUrl;
  const fallbackStyle = { backgroundImage: `url("${fallBackUrl}")` };
  const loaded = Boolean(loadedImages[no]);
  const imgStyle = { backgroundImage: `url("${mainImgUrl}")` };
  const displayMainImg = Boolean((loaded || loading) && !throttle && !saveBandwidth);

  return (
    <div className={classes.frame} ref={imgRef}>
      <div className={classes.imgWrap}>
        <div className={classes.currentImg}>
          <div className={classes.imgBack} style={fallbackStyle} />
          <div className={classes.imgMain} style={fallbackStyle} />
          {displayMainImg && <div className={classes.imgMain} style={imgStyle} />}
        </div>
      </div>
      <div>
        <div className={classes.textBox}>
          <div className={classes.en}>{text}</div>
          <div className={classes.pl}>{translation}</div>
        </div>
      </div>
    </div>
  );
}

let styleTimeout = 0;
const keys = ['Space'];

function Player(props) {
  const classes = useStyles();
  const [no, setNo] = useState(0);
  const [img1No, setImg1No] = useState(0);
  const [img2No, setImg2No] = useState(1);
  const [throttle, setThrottle] = useState(false);
  const [saveBandwidth, setSaveBandwidth] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [keyPress, setKeyPress] = useState(null);

  const throttleRef = useRef();
  const mounted = useRef(true);
  const { exercise, status, setSoundEnabled, play, pause, data, time, keyboard } = props;
  const timeRef = useRef(0);
  const img1Ref = useRef(null);
  const img2Ref = useRef(null);
  const playRef = useRef(null);
  const stopRef = useRef(null);
  const keyTimeoutRef = useRef(0);

  useEffect(() => {
    props.startStats(props.type);
    const localSettings = getLocalSettings();
    const { saveBandwidth } = localSettings;
    setSaveBandwidth(saveBandwidth);
    const keyboardSubscription = keyboard.subject.subscribe(handleKeyUp);
    return () => {
      keyboardSubscription.unsubscribe();
      pause();
      mounted.current = false;
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    update();
    // eslint-disable-next-line
  }, [time]);

  useEffect(() => {
    if (!keyPress) return;
    switch (keyPress) {
      case 'Space':
        togglePlay();
        break;
    }
    clearTimeout(keyTimeoutRef);
    keyTimeoutRef.current = setTimeout(() => {
      if (!mounted.current) return;
      setKeyPress(null);
    }, 100);
    // eslint-disable-next-line
  }, [keyPress]);

  const handleKeyUp = (keyCode) => {
    if (keys.indexOf(keyCode) === -1) return;
    setKeyPress(keyCode);
  }

  const update = () => {
    const slideNo = getNo();
    if (slideNo !== no) advanceSlide(slideNo);
  }

  const advanceSlide = (slideNo) => {
    setNo(slideNo);
    const img1 = img1Ref.current;
    if (throttle) {
      clearTimeout(timeRef.current);
      img1.style.transition = 'none';
      img1.style.opacity = 1;
      setImg1No(slideNo);
      const nextImgNo = slideNo === exercise.length - 1 ? slideNo : slideNo + 1;
      setImg2No(nextImgNo);
      return;
    }
    img1.style.transition = 'all 0.5s';
    img1.style.opacity = 0;
    advanceImg1(slideNo);
  }

  const advanceImg1 = (slideNo) => {
    if (!mounted.current) return;
    clearTimeout(timeRef.current);
    timeRef.current = setTimeout(() => {
      if (!mounted.current) return;
      setImg1No(slideNo);
      advanceImg2(slideNo);
    }, 500);
  }

  const advanceImg2 = (slideNo) => {
    if (!mounted.current) return;
    timeRef.current = setTimeout(() => {
      if (!mounted.current) return;
      setImg1No(slideNo);
      img1Ref.current.style.opacity = 1;
      advanceImg3(slideNo);
    }, 500);
  }

  const advanceImg3 = (slideNo) => {
    if (!mounted.current) return;
    timeRef.current = setTimeout(() => {
      if (!mounted.current) return;
      const nextImgNo = slideNo === exercise.length - 1 ? slideNo : slideNo + 1;
      setImg2No(nextImgNo);
    }, 500);
  }

  const getNo = () => {
    const pos = parseInt(time * 1000);
    return data.reduce((t, val, idx) => {
      if (!idx) {
        if (pos <= val.e) return idx;
        return t;
      } else {
        if (pos >= data[idx - 1].e) return idx;
        return t;
      }
    }, 0);
  }

  const getImgUrl = (no) => staticsUrl + exercise[no].imageUrl;

  const onImgLoad = () => {
    setLoadedImages({ ...loadedImages, [loadingQueue]: true });
    setLoadingQueue(null);
  }

  const mainImgPreloader = () => {
    let imgUrl = null;
    if (throttle) return;
    if (loadingQueue !== null) {
      imgUrl = getImgUrl(loadingQueue);
    } else {
      if (!loadedImages[no]) {
        setLoadingQueue(no);
      } else {
        if (!loadedImages[no + 1] && no < exercise.length - 1) {
          setLoadingQueue(no + 1);
        }
      }
    }
    return (<>
      {imgUrl !== null && <img src={imgUrl} alt="" className={classes.preloads} onLoad={onImgLoad} />}
    </>);
  }

  const onSliderChange = () => {
    clearTimeout(throttleRef.current);
    if (!throttle) setThrottle(true);
    throttleRef.current = setTimeout(() => {
      setThrottle(false);
    }, 500);
  }

  const togglePlay = () => {
    let icon;
    if (status === 'playing') {
      pause();
      icon = stopRef.current;
    } else {
      play();
      icon = playRef.current;
    }
    clearTimeout(styleTimeout);
    icon.style.transition = 'none';
    icon.style.opacity = 1;
    setTimeout(() => {
      if (!mounted.current) return;
      icon.style.transition = 'opacity 1s';
      icon.style.opacity = 0;
    }, 200);
  }

  const renderStartStop = () => {
    return (
      <div className={classes.startStop}>
        <i className={classes.playBtn} onClick={togglePlay} ref={playRef}>
          <PlayIcon />
        </i>
        <i className={classes.playBtn} onClick={togglePlay} ref={stopRef}>
          <StopIcon />
        </i>
      </div>
    );
  }

  return (<>
    <div className={classes.imgContainer}>
      {mainImgPreloader()}
      <Image
        throttle={throttle}
        no={img2No}
        exercise={exercise}
        saveBandwidth={saveBandwidth}
        imgRef={img2Ref}
        loadedImages={loadedImages}
        loading={loadingQueue === no}
        status={status}
        play={play}
        pause={pause}
        mounted={mounted}
      />
      <Image
        throttle={throttle}
        no={img1No}
        exercise={exercise}
        saveBandwidth={saveBandwidth}
        imgRef={img1Ref}
        loadedImages={loadedImages}
        loading={loadingQueue === no}
        status={status}
        play={play}
        pause={pause}
        mounted={mounted}
      />
    </div>
    {renderStartStop()}
    {!props.expand && <Transport
      no={no}
      setNo={(no2) => setNo(no2)}
      len={exercise.length}
      play={play}
      pause={pause}
      status={props.status}
      type={props.type}
      time={time}
      media={props.media}
      setSoundEnabled={setSoundEnabled}
      onSliderChange={onSliderChange}
    />}
  </>);

}

const mapStateToProps = (state) => {
  return {
    mobile: state.mobile,
    media: state.media,
    browser: state.auth.browser.system.browser.name,
    keyboard: state.keyboard,
  }
}

export default connect(mapStateToProps)(Player);
